<app-component-level-error-message [errorMessage]="errorMessage"></app-component-level-error-message>

@if (uiState === uiStateForTemplate.ShowLoading) {
  <app-loading-card-content></app-loading-card-content>
} @else if (uiState === uiStateForTemplate.ShowData && comprehensiveReport) {
  <div class="margin-top-large">
    <div class="justify-space-between flex-row align-start">
      <div>
        <div class="section-header">Comprehensive Game Report</div>
        <div class="ticket-type-header">Date Range:
          {{ dateRanges()?.StartDate | date: 'MMM d, y HH:mm' }} -
          {{ dateRanges()?.EndDate | date: 'MMM d, y HH:mm' }}
        </div>
      </div>
      <div class="report-header-actions">
        <div class="help-controls">
          <button mat-icon-button
                  class="glossary-button"
                  matTooltip="View field glossary"
                  (click)="openGlossary()">
            <mat-icon>menu_book</mat-icon>
          </button>
          <span class="help-toggle-label">Show Help</span>
          <mat-slide-toggle
            data-testid="comprehensive-report-help-toggle"
            [checked]="showHelp()"
            (change)="showHelp.set($event.checked)">
          </mat-slide-toggle>
        </div>
        <button mat-raised-button color="primary" (click)="generatePDF()"
                data-testid="comprehensive-report-generate-pdf-button">Generate PDF</button>
        <button mat-raised-button class="margin-left-small" (click)="downloadAsCSV()"
                data-testid="comprehensive-report-generate-csv-button">Generate CSV</button>
      </div>
    </div>

    <div class="margin-top-large">
      <div class="report-section">
        <h4 class="report-section-title">Sales Breakdown</h4>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Gross Sales <app-field-help-icon fieldKey="comprehensiveGrossSales"></app-field-help-icon></span>
            <span class="report-value" data-testid="comprehensive-report-gross-total">{{ comprehensiveReport.FinancialTotals.GrossTotalSales | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description" data-testid="comprehensive-report-field-description">{{ fieldDescriptions['comprehensiveGrossSales'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Dispute Fees <app-field-help-icon fieldKey="comprehensiveDisputeFees"></app-field-help-icon></span>
            <span class="report-value red-text" data-testid="comprehensive-stripe-dispute-fees">(-{{ comprehensiveReport.FinancialTotals.StripeDisputeFees | formatCurrency }})</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['comprehensiveDisputeFees'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Charity Host Admin Fees <app-field-help-icon fieldKey="comprehensiveAdminFees"></app-field-help-icon></span>
            <span class="report-value red-text" data-testid="comprehensive-report-admin-commission">(-{{ comprehensiveReport.FinancialTotals.AdministrativeCommissionTotal | formatCurrency }})</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['comprehensiveAdminFees'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Causable Fees <app-field-help-icon fieldKey="comprehensiveCausableFees"></app-field-help-icon></span>
            <span class="report-value red-text" data-testid="comprehensive-report-causable-fees">(-{{ comprehensiveReport.FinancialTotals.CausableFees | formatCurrency }})</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['comprehensiveCausableFees'] }}</div> }
        </div>

        @if (comprehensiveReport.FinancialTotals.GstTotal && comprehensiveReport.FinancialTotals.GstTotal > 0) {
          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">GST on Causable Fees <app-field-help-icon fieldKey="comprehensiveGstOnCausable"></app-field-help-icon></span>
              <span class="report-value red-text" data-testid="comprehensive-report-gst-on-causable">(-{{ comprehensiveReport.FinancialTotals.GstTotal | formatCurrency }})</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['comprehensiveGstOnCausable'] }}</div> }
          </div>
        }
        @if (comprehensiveReport.FinancialTotals.PstTotal && comprehensiveReport.FinancialTotals.PstTotal > 0) {
          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">{{ pstLabel() }} on Causable Fees <app-field-help-icon fieldKey="comprehensivePstOnCausable"></app-field-help-icon></span>
              <span class="report-value red-text" data-testid="comprehensive-report-pst-on-causable">(-{{ comprehensiveReport.FinancialTotals.PstTotal | formatCurrency }})</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['comprehensivePstOnCausable'] }}</div> }
          </div>
        }
        @if (comprehensiveReport.FinancialTotals.HstTotal && comprehensiveReport.FinancialTotals.HstTotal > 0) {
          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">HST on Causable Fees <app-field-help-icon fieldKey="comprehensiveHstOnCausable"></app-field-help-icon></span>
              <span class="report-value red-text" data-testid="comprehensive-report-causable-fees-tax">(-{{ comprehensiveReport.FinancialTotals.HstTotal | formatCurrency }})</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['comprehensiveHstOnCausable'] }}</div> }
          </div>
        }
        @if (!comprehensiveReport.FinancialTotals.GstTotal && !comprehensiveReport.FinancialTotals.HstTotal && comprehensiveReport.FinancialTotals.CausableFeesTax > 0) {
          <div class="report-row-wrapper">
            <div class="report-row">
              <span class="report-label">HST on Causable Fees <app-field-help-icon fieldKey="comprehensiveHstOnCausable"></app-field-help-icon></span>
              <span class="report-value red-text" data-testid="comprehensive-report-causable-fees-tax">(-{{ comprehensiveReport.FinancialTotals.CausableFeesTax | formatCurrency }})</span>
            </div>
            @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['comprehensiveHstOnCausable'] }}</div> }
          </div>
        }

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Stripe Fees <app-field-help-icon fieldKey="comprehensiveStripeFees"></app-field-help-icon></span>
            <span class="report-value red-text" data-testid="comprehensive-report-payment-processor-fees">(-{{ comprehensiveReport.FinancialTotals.StripeFees | formatCurrency }})</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['comprehensiveStripeFees'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Refund Processing Fees <app-field-help-icon fieldKey="comprehensiveCharityRefundFees"></app-field-help-icon></span>
            <span class="report-value red-text" data-testid="comprehensive-report-refund-fees">(-{{ comprehensiveReport.FinancialTotals.CharityRefundContributionTotal | formatCurrency }})</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['comprehensiveCharityRefundFees'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Charity Dispute Fees <app-field-help-icon fieldKey="comprehensiveCharityDisputeFees"></app-field-help-icon></span>
            <span class="report-value red-text" data-testid="comprehensive-report-dispute-fees">(-{{ comprehensiveReport.FinancialTotals.CharityDisputeContributionTotal | formatCurrency }})</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['comprehensiveCharityDisputeFees'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Allocated to Prizes <app-field-help-icon fieldKey="comprehensivePrizeAmount"></app-field-help-icon></span>
            <span class="report-value red-text" data-testid="comprehensive-report-prize-total">(-{{ comprehensiveReport.FinancialTotals.NetPotContributionTotal | formatCurrency }})</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['comprehensivePrizeAmount'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row summary-row">
            <span class="report-label bold-text">Net Proceeds For Charity <app-field-help-icon fieldKey="comprehensiveNetProceedsForCharity"></app-field-help-icon></span>
            <span class="report-value bold-text" [class.green-text]="comprehensiveReport.FinancialTotals.NetCharityContribution > 0" [class.red-text]="comprehensiveReport.FinancialTotals.NetCharityContribution <= 0" data-testid="comprehensive-report-charity-proceeds">{{ comprehensiveReport.FinancialTotals.NetCharityContribution | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['comprehensiveNetProceedsForCharity'] }}</div> }
        </div>
      </div>

      <div class="report-section">
        <h4 class="report-section-title">Prizes Paid</h4>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Prizes Awarded <app-field-help-icon fieldKey="comprehensivePrizesAwarded"></app-field-help-icon></span>
            <span class="report-value" data-testid="comprehensive-report-prizes-awarded">{{ comprehensiveReport.FinancialTotals.PrizesAwardedTotal | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['comprehensivePrizesAwarded'] }}</div> }
        </div>
      </div>

      <div class="report-section">
        <h4 class="report-section-title">Game Counts</h4>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Draws with no Winner <app-field-help-icon fieldKey="drawsWithNoWinner"></app-field-help-icon></span>
            <span class="report-value" data-testid="comprehensive-report-draws-without-winners">{{ comprehensiveReport.GameWinnerData.NumberOfDrawsWithNoWinners }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['drawsWithNoWinner'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Draws with a winner <app-field-help-icon fieldKey="drawsWithWinner"></app-field-help-icon></span>
            <span class="report-value" data-testid="comprehensive-report-draws-with-winners">{{ comprehensiveReport.GameWinnerData.NumberOfDrawsWithWinners }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['drawsWithWinner'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row summary-row">
            <span class="report-label bold-text">Total Number of Draws <app-field-help-icon fieldKey="totalDraws"></app-field-help-icon></span>
            <span class="report-value bold-text" data-testid="comprehensive-report-total-draws">{{ comprehensiveReport.GameWinnerData.TotalNumberOfDraws }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['totalDraws'] }}</div> }
        </div>
      </div>

      <div class="report-section">
        <h4 class="report-section-title">Player Numbers</h4>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Total numbers played <app-field-help-icon fieldKey="totalNumbersPlayed"></app-field-help-icon></span>
            <span class="report-value" data-testid="comprehensive-report-tickets-played">{{ comprehensiveReport.TicketCounts.NumberOfTicketsPlayed }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['totalNumbersPlayed'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Total unplayed numbers <app-field-help-icon fieldKey="totalUnplayedNumbers"></app-field-help-icon></span>
            <span class="report-value" data-testid="comprehensive-report-tickets-unplayed">{{ comprehensiveReport.TicketCounts.NumberOfTicketsNotPlayed }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['totalUnplayedNumbers'] }}</div> }
        </div>
      </div>

      <div class="report-section">
        <h4 class="report-section-title">Players</h4>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Played <app-field-help-icon fieldKey="playersPlayed"></app-field-help-icon></span>
            <span class="report-value" data-testid="comprehensive-report-active-users">{{ comprehensiveReport.UserCounts.NumberOfActiveUsers }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['playersPlayed'] }}</div> }
        </div>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Not Played <app-field-help-icon fieldKey="playersNotPlayed"></app-field-help-icon></span>
            <span class="report-value" data-testid="comprehensive-report-inactive-users">{{ comprehensiveReport.UserCounts.NumberOfInactiveUsers }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['playersNotPlayed'] }}</div> }
        </div>
      </div>

      <div class="report-section">
        <h4 class="report-section-title">Group/Charity Breakdown</h4>

        <div class="report-row-wrapper">
          <div class="report-row">
            <span class="report-label">Total Charity Contribution <app-field-help-icon fieldKey="totalCharityContribution"></app-field-help-icon></span>
            <span class="report-value" data-testid="comprehensive-report-financial-charity-contribution">{{ comprehensiveReport.FinancialTotals.NetCharityContribution | formatCurrency }}</span>
          </div>
          @if (showHelp()) { <div class="field-description">{{ fieldDescriptions['totalCharityContribution'] }}</div> }
        </div>

        <div class="charity-grid-section">
          <ag-grid-angular
            class="ag-theme-quartz"
            data-testid="comprehensive-charity-contribution-grid"
            (gridReady)="onGridReady($event)"
            [animateRows]="true"
            [domLayout]="'autoHeight'"
            overlayNoRowsTemplate="<span>No tickets for this game</span>"
            [rowData]="comprehensiveReport.CharityBreakdown"
            [columnDefs]="groupBreakdownReportDefs">
          </ag-grid-angular>
        </div>
      </div>
    </div>
  </div>
}
